import type { AuthUserFacingError, SignInInput, SignUpInput } from "@andrum/schemas";
import type { ProfileRow } from "@andrum/database";
import { getSupabase, isSupabaseConfigured } from "@/lib/supabase/client";
import { t } from "@/i18n";

export type AuthResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: AuthUserFacingError; message: string };

function mapAuthError(error: { message: string }): {
  error: AuthUserFacingError;
  message: string;
} {
  const msg = error.message.toLowerCase();
  if (msg.includes("invalid login") || msg.includes("invalid credentials")) {
    return {
      error: "INVALID_CREDENTIALS",
      message: t("errors.wrongCredentials"),
    };
  }
  if (msg.includes("already registered") || msg.includes("already been registered")) {
    return {
      error: "EMAIL_IN_USE",
      message: t("errors.emailTaken"),
    };
  }
  if (msg.includes("password")) {
    return {
      error: "WEAK_PASSWORD",
      message: t("errors.weakPassword"),
    };
  }
  if (msg.includes("network") || msg.includes("fetch")) {
    return {
      error: "NETWORK_ERROR",
      message: t("errors.network"),
    };
  }
  return {
    error: "UNKNOWN_ERROR",
    message: t("errors.generic"),
  };
}

export async function signInWithPassword(
  input: SignInInput
): Promise<AuthResult<{ userId: string }>> {
  const supabase = getSupabase();
  if (!supabase) {
    return {
      ok: false,
      error: "NOT_CONFIGURED",
      message: t("errors.authNotConfigured"),
    };
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email: input.email,
    password: input.password,
  });

  if (error || !data.user) {
    const mapped = mapAuthError({ message: error?.message ?? "unknown" });
    return { ok: false, ...mapped };
  }

  return { ok: true, data: { userId: data.user.id } };
}

export async function signUpWithPassword(
  input: SignUpInput
): Promise<AuthResult<{ userId: string; needsEmailConfirmation: boolean }>> {
  const supabase = getSupabase();
  if (!supabase) {
    return {
      ok: false,
      error: "NOT_CONFIGURED",
      message: t("errors.signupNotConfigured"),
    };
  }

  const options =
    input.displayName !== undefined
      ? { data: { display_name: input.displayName } }
      : {};

  const { data, error } = await supabase.auth.signUp({
    email: input.email,
    password: input.password,
    options,
  });

  if (error || !data.user) {
    const mapped = mapAuthError({ message: error?.message ?? "unknown" });
    return { ok: false, ...mapped };
  }

  // Fire-and-forget ops notify (adam@sprow.co) — never block signup UX
  void notifyOpsSignup({
    email: input.email,
    userId: data.user.id,
    source: "app",
    accessToken: data.session?.access_token ?? null,
    ...(input.displayName ? { name: input.displayName } : {}),
  });

  return {
    ok: true,
    data: {
      userId: data.user.id,
      needsEmailConfirmation: !data.session,
    },
  };
}

/** Notify ops of new signup ([SIGNUP] channel). Soft-fail always. */
async function notifyOpsSignup(input: {
  email: string;
  userId: string;
  name?: string;
  source: "app" | "practice";
  accessToken: string | null;
}): Promise<void> {
  try {
    const { API_BASE } = await import("@/lib/api/session");
    const payload = {
      source: input.source,
      ...(input.name ? { name: input.name } : {}),
    };
    if (input.accessToken) {
      await fetch(`${API_BASE}/api/v1/ops/signup`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${input.accessToken}`,
        },
        body: JSON.stringify(payload),
      });
      return;
    }
    await fetch(`${API_BASE}/api/v1/public/signup-notify`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        email: input.email,
        userId: input.userId,
        ...payload,
      }),
    });
  } catch {
    // ignore — ops notify must never break auth
  }
}

export async function signOut(): Promise<AuthResult<null>> {
  const supabase = getSupabase();
  if (!supabase) {
    return { ok: true, data: null };
  }
  const { error } = await supabase.auth.signOut();
  if (error) {
    const mapped = mapAuthError({ message: error.message });
    return { ok: false, ...mapped };
  }
  return { ok: true, data: null };
}

export async function signInWithMagicLink(
  email: string
): Promise<AuthResult<null>> {
  const supabase = getSupabase();
  if (!supabase) {
    return {
      ok: false,
      error: "NOT_CONFIGURED",
      message: t("errors.authNotConfigured"),
    };
  }

  const emailOk = email.trim().toLowerCase();
  if (!emailOk.includes("@")) {
    return {
      ok: false,
      error: "VALIDATION_ERROR",
      message: t("errors.invalidEmail"),
    };
  }

  const redirectTo =
    typeof window !== "undefined"
      ? `${window.location.origin}/`
      : "https://app.andrum.app/";

  const { error } = await supabase.auth.signInWithOtp({
    email: emailOk,
    options: {
      emailRedirectTo: redirectTo,
      shouldCreateUser: true,
    },
  });

  if (error) {
    const mapped = mapAuthError({ message: error.message });
    return { ok: false, ...mapped };
  }

  return { ok: true, data: null };
}

export async function updateDisplayName(
  displayName: string
): Promise<AuthResult<ProfileRow>> {
  const supabase = getSupabase();
  if (!supabase) {
    return {
      ok: false,
      error: "NOT_CONFIGURED",
      message: t("errors.supabaseMissing"),
    };
  }

  const trimmed = displayName.trim();
  if (trimmed.length < 1 || trimmed.length > 80) {
    return {
      ok: false,
      error: "VALIDATION_ERROR",
      message: t("errors.nameLength"),
    };
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return {
      ok: false,
      error: "AUTH_REQUIRED",
      message: t("errors.needLogin"),
    };
  }

  const { data, error } = await supabase
    .from("profiles")
    .update({ display_name: trimmed })
    .eq("id", user.id)
    .select("*")
    .single();

  if (error || !data) {
    return {
      ok: false,
      error: "UNKNOWN_ERROR",
      message: t("errors.saveNameFail"),
    };
  }

  // Keep auth metadata loosely in sync (non-fatal if it fails)
  await supabase.auth.updateUser({
    data: { display_name: trimmed },
  });

  return { ok: true, data };
}

export async function requestPasswordReset(
  email: string
): Promise<AuthResult<null>> {
  const supabase = getSupabase();
  if (!supabase) {
    return {
      ok: false,
      error: "NOT_CONFIGURED",
      message: t("errors.resetNotConfigured"),
    };
  }

  const redirectTo =
    typeof window !== "undefined"
      ? `${window.location.origin}/login`
      : "https://app.andrum.app/login";

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo,
  });

  if (error) {
    const mapped = mapAuthError({ message: error.message });
    return { ok: false, ...mapped };
  }

  return { ok: true, data: null };
}

export async function fetchOwnProfile(): Promise<AuthResult<ProfileRow | null>> {
  const supabase = getSupabase();
  if (!supabase) {
    return {
      ok: false,
      error: "NOT_CONFIGURED",
      message: t("errors.supabaseMissing"),
    };
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return { ok: true, data: null };
  }

  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", user.id)
    .maybeSingle();

  if (error) {
    return {
      ok: false,
      error: "UNKNOWN_ERROR",
      message: t("errors.profileFetch"),
    };
  }

  return { ok: true, data };
}

export async function completeOnboardingRemote(input: {
  preferredCoachTone?: string | undefined;
  availableMinutes?: number | undefined;
  goals: string[];
  lifeSituations?: string[];
  joyPathway?: string | null;
  calmPathway?: string | null;
}): Promise<AuthResult<ProfileRow>> {
  const supabase = getSupabase();
  if (!supabase) {
    return {
      ok: false,
      error: "NOT_CONFIGURED",
      message: t("errors.supabaseMissing"),
    };
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return {
      ok: false,
      error: "AUTH_REQUIRED",
      message: t("errors.needLogin"),
    };
  }

  const now = new Date().toISOString();

  // Upsert so first-time users without a trigger-created row still persist
  const { data: profile, error: profileError } = await supabase
    .from("profiles")
    .upsert(
      {
        id: user.id,
        onboarding_completed_at: now,
        preferred_coach_tone: input.preferredCoachTone ?? null,
        available_minutes: input.availableMinutes ?? null,
        joy_pathway: input.joyPathway ?? null,
        calm_pathway: input.calmPathway ?? null,
        updated_at: now,
      },
      { onConflict: "id" }
    )
    .select("*")
    .single();

  if (profileError || !profile) {
    return {
      ok: false,
      error: "UNKNOWN_ERROR",
      message: "Kunde inte spara onboarding.",
    };
  }

  if (input.goals.length > 0) {
    await supabase.from("user_goals").delete().eq("user_id", user.id);
    const rows = input.goals.map((goal_code, index) => ({
      user_id: user.id,
      goal_code,
      priority: index + 1,
      active: true,
    }));
    const { error: goalsError } = await supabase.from("user_goals").insert(rows);
    if (goalsError) {
      console.warn("Failed to save user goals", goalsError.message);
    }
  }

  const situations = (input.lifeSituations ?? []).filter(
    (s) => s && s !== "prefer_not_to_say"
  );
  await supabase.from("user_life_situations").delete().eq("user_id", user.id);
  if (situations.length > 0) {
    const { error: lifeError } = await supabase
      .from("user_life_situations")
      .insert(
        situations.map((situation_code) => ({
          user_id: user.id,
          situation_code,
        }))
      );
    if (lifeError) {
      console.warn("Failed to save life situations", lifeError.message);
    }
  }

  await supabase.from("consents").insert({
    user_id: user.id,
    consent_type: "terms_and_privacy",
    policy_version: "2026-07-01",
    granted: true,
    granted_at: now,
    source: "onboarding",
  });

  return { ok: true, data: profile };
}

/** Update goals, life situations, and joy/calm pathways without redoing full onboarding. */
export async function updatePreferencesRemote(input: {
  goals?: string[];
  lifeSituations?: string[];
  joyPathway?: string | null;
  calmPathway?: string | null;
}): Promise<AuthResult<ProfileRow>> {
  const supabase = getSupabase();
  if (!supabase) {
    return {
      ok: false,
      error: "NOT_CONFIGURED",
      message: t("errors.supabaseMissing"),
    };
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return {
      ok: false,
      error: "AUTH_REQUIRED",
      message: t("errors.needLogin"),
    };
  }

  const now = new Date().toISOString();
  const profilePatch: {
    updated_at: string;
    joy_pathway?: string | null;
    calm_pathway?: string | null;
  } = { updated_at: now };
  if (input.joyPathway !== undefined) {
    profilePatch.joy_pathway = input.joyPathway;
  }
  if (input.calmPathway !== undefined) {
    profilePatch.calm_pathway = input.calmPathway;
  }

  const { data: profile, error: profileError } = await supabase
    .from("profiles")
    .update(profilePatch)
    .eq("id", user.id)
    .select("*")
    .single();

  if (profileError || !profile) {
    return {
      ok: false,
      error: "UNKNOWN_ERROR",
      message: t("errors.generic"),
    };
  }

  if (input.goals !== undefined) {
    await supabase.from("user_goals").delete().eq("user_id", user.id);
    if (input.goals.length > 0) {
      const rows = input.goals.map((goal_code, index) => ({
        user_id: user.id,
        goal_code,
        priority: index + 1,
        active: true,
      }));
      const { error: goalsError } = await supabase
        .from("user_goals")
        .insert(rows);
      if (goalsError) {
        console.warn("Failed to save user goals", goalsError.message);
      }
    }
  }

  if (input.lifeSituations !== undefined) {
    const situations = input.lifeSituations.filter(
      (s) => s && s !== "prefer_not_to_say"
    );
    await supabase.from("user_life_situations").delete().eq("user_id", user.id);
    if (situations.length > 0) {
      const { error: lifeError } = await supabase
        .from("user_life_situations")
        .insert(
          situations.map((situation_code) => ({
            user_id: user.id,
            situation_code,
          }))
        );
      if (lifeError) {
        console.warn("Failed to save life situations", lifeError.message);
      }
    }
  }

  return { ok: true, data: profile };
}

export async function loadUserPreferencesRemote(): Promise<
  AuthResult<{
    goals: string[];
    lifeSituations: string[];
    joyPathway: string | null;
    calmPathway: string | null;
  }>
> {
  const supabase = getSupabase();
  if (!supabase) {
    return {
      ok: false,
      error: "NOT_CONFIGURED",
      message: t("errors.supabaseMissing"),
    };
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return {
      ok: false,
      error: "AUTH_REQUIRED",
      message: t("errors.needLogin"),
    };
  }

  const [goalsRes, lifeRes, profileRes] = await Promise.all([
    supabase
      .from("user_goals")
      .select("goal_code")
      .eq("user_id", user.id)
      .eq("active", true)
      .order("priority", { ascending: true }),
    supabase
      .from("user_life_situations")
      .select("situation_code")
      .eq("user_id", user.id),
    supabase
      .from("profiles")
      .select("joy_pathway, calm_pathway")
      .eq("id", user.id)
      .maybeSingle(),
  ]);

  return {
    ok: true,
    data: {
      goals: (goalsRes.data ?? []).map((g) => g.goal_code),
      lifeSituations: (lifeRes.data ?? []).map((r) => r.situation_code),
      joyPathway: profileRes.data?.joy_pathway ?? null,
      calmPathway: profileRes.data?.calm_pathway ?? null,
    },
  };
}

export function authConfigured(): boolean {
  return isSupabaseConfigured();
}

/** Clear onboarding flag in DB so user can redo the flow from profile. */
export async function restartOnboardingRemote(): Promise<AuthResult<ProfileRow>> {
  const supabase = getSupabase();
  if (!supabase) {
    return {
      ok: false,
      error: "NOT_CONFIGURED",
      message: t("errors.supabaseMissing"),
    };
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return {
      ok: false,
      error: "AUTH_REQUIRED",
      message: t("errors.needLogin"),
    };
  }

  const { data: profile, error } = await supabase
    .from("profiles")
    .upsert(
      {
        id: user.id,
        onboarding_completed_at: null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "id" }
    )
    .select("*")
    .single();

  if (error || !profile) {
    return {
      ok: false,
      error: "UNKNOWN_ERROR",
      message: t("errors.onboardingRestartFail"),
    };
  }

  return { ok: true, data: profile };
}
